import { useEffect, useState } from 'react';

/**
 * The "tablet" layout query shared by list pages and dialogs (UI/UX hand-off W-iP6, W-iP10).
 *
 * A standard iPad is 1180 x 820 in landscape and 820 x 1180 in portrait, so `max-width: 1180px`
 * covers both orientations; `pointer: coarse` catches a touch-first device that is wider than
 * that. It is a media query, never a user-agent sniff: an iPad with a trackpad attached at
 * 1180px still gets the tablet layout because of its width, and a desktop at 1366px and up
 * never does.
 *
 * Mirrored in CSS by the `@media (max-width: 1180px), (pointer: coarse)` blocks in
 * `tailwind.css` (the full-screen dialog sheet and the compact task stepper) - keep them in
 * sync.
 */
export const TABLET_LAYOUT_QUERY = '(max-width: 1180px), (pointer: coarse)';

/**
 * When filter panels start collapsed: on a tablet, and on a short laptop screen (1366 x 768,
 * 1536 x 864), where an expanded filter panel plus the header left the table 1-3 rows (W-H5).
 * From 901px tall (a 1920 x 1080 desktop) they start expanded.
 */
export const COMPACT_FILTERS_QUERY = `${TABLET_LAYOUT_QUERY}, (max-height: 900px)`;

const matches = (query: string): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(query).matches
    : false;

/** True while the viewport matches {@link TABLET_LAYOUT_QUERY}. False where matchMedia is absent (jsdom). */
export const useTabletLayout = (query: string = TABLET_LAYOUT_QUERY): boolean => {
  const [isTablet, setIsTablet] = useState<boolean>(() => matches(query));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setIsTablet(e.matches);
    setIsTablet(mql.matches);
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    // Older Safari only has the deprecated listener API.
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return isTablet;
};

export default useTabletLayout;
